import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useSpring, animated } from '@react-spring/web';

/**
 * Styled container for the animated button
 */
const ButtonContainer = styled.div`
  cursor: pointer;
  display: inline-block;
  
  /* Apply styles only when using a non-blank skin */
  ${({ theme }) => theme.name !== 'blank' && `
    cursor: pointer;
    user-select: none;
  `}
`;

/**
 * AnimatedButton - A theme-aware button with animation effects
 * 
 * @param {Object} props - Component props
 * @param {number} [props.speed=1] - Animation speed multiplier (higher = faster)
 * @param {string} [props.text] - Button text (overrides theme text)
 * @param {function} [props.onClick] - Click handler function
 * @param {Object} props.restProps - Additional props passed to the SVG
 * 
 * @returns {React.Component} Themed animated button
 */
const AnimatedButton = ({ 
  speed = 1, 
  text, 
  onClick,
  ...restProps 
}) => {
  const [volume, setVolume] = useState(speed);

  // Update animation speed when speed prop changes
  useEffect(() => {
    setVolume(speed);
  }, [speed]);

  // Get animation configuration from theme or use defaults
  const getThemeValue = (prop, defaultValue) => {
    if (!restProps.theme || restProps.theme.name === 'blank') {
      return defaultValue;
    }
    return prop;
  };

  // Animation settings with react-spring
  const styles = useSpring({
    from: { transform: 'rotate(0deg)' },
    to: async (next) => {
      while (true) {
        // Get the min and max angles from theme or use defaults
        const minAngle = getThemeValue(
          restProps.theme?.animatedButton?.animation?.minAngle, 
          '-10deg'
        );
        const maxAngle = getThemeValue(
          restProps.theme?.animatedButton?.animation?.maxAngle, 
          '10deg'
        );
        
        await next({ transform: `rotate(${maxAngle})` });
        await next({ transform: `rotate(${minAngle})` });
      }
    },
    // Animation speed based on volume and theme setting
    config: { 
      duration: getThemeValue(
        restProps.theme?.animatedButton?.animation?.baseSpeed, 
        1000
      ) / volume 
    },
  });

  // Get the number of layers from theme or use default
  const layers = getThemeValue(
    restProps.theme?.animatedButton?.layers, 
    5
  );
  
  // Get button dimensions from theme or use defaults
  const width = getThemeValue(
    restProps.theme?.animatedButton?.width, 
    '200'
  );
  
  const height = getThemeValue(
    restProps.theme?.animatedButton?.height, 
    '100'
  );
  
  const borderRadius = getThemeValue(
    restProps.theme?.animatedButton?.borderRadius, 
    '10'
  );
  
  // Get colors from theme or use defaults
  const backgroundColor = getThemeValue(
    restProps.theme?.animatedButton?.backgroundColor, 
    'rgba(0, 123, 255, 0.2)'
  );
  
  const textColor = getThemeValue(
    restProps.theme?.animatedButton?.textColor, 
    'white'
  );
  
  // Get button text from props or theme or use default
  const buttonText = text || getThemeValue(
    restProps.theme?.animatedButton?.text, 
    'Click Me'
  );

  return (
    <ButtonContainer onClick={onClick}>
      <animated.svg
        style={restProps.theme?.name !== 'blank' ? styles : {}}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        xmlns="http://www.w3.org/2000/svg"
        {...restProps}
      >
        {/* Create the layered rectangles effect */}
        {Array.from({ length: layers }).map((_, index) => (
          <rect
            key={index}
            x="10"
            y="10"
            width={parseInt(width) - 20}
            height={parseInt(height) - 20}
            rx={borderRadius}
            ry={borderRadius}
            fill={backgroundColor}
            style={{ opacity: 1 - index * (1 / layers) }}
          />
        ))}
        <text 
          x={parseInt(width) / 2} 
          y={parseInt(height) / 2} 
          textAnchor="middle" 
          alignmentBaseline="middle" 
          fill={textColor}
        >
          {buttonText}
        </text>
      </animated.svg>
    </ButtonContainer>
  );
};

AnimatedButton.propTypes = {
  /** Animation speed multiplier */
  speed: PropTypes.number,
  /** Button text (overrides theme setting) */
  text: PropTypes.string,
  /** Click handler function */
  onClick: PropTypes.func
};

export default AnimatedButton;

